/* CAVE — landing motion and small behaviours (ES module: runs once per page no
   matter how many sections include it).
   Principles: native scrolling only, no pinning, transform/opacity only, text is
   revealed once and then stays still, content is fully visible without this
   file, reduced motion respected, and every section cleans up after itself when
   the Theme Editor reloads or removes it.

   Horizon may scroll an inner container (.page-wrapper) instead of the window,
   so reveals use IntersectionObserver (works with any scroller) and
   scroll-linked effects attach to whichever element really scrolls. */

const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
const assetBase = new URL('.', import.meta.url).href;

let gsapPromise;
function loadScript(src) {
  return new Promise((resolve, reject) => {
    const s = document.createElement('script');
    s.src = src; s.async = true;
    s.onload = resolve; s.onerror = reject;
    document.head.appendChild(s);
  });
}
function getGsap() {
  if (window.gsap && window.ScrollTrigger) {
    window.gsap.registerPlugin(window.ScrollTrigger);
    return Promise.resolve(window.gsap);
  }
  gsapPromise ??= loadScript(assetBase + 'gsap.min.js')
    .then(() => loadScript(assetBase + 'gsap-scrolltrigger.min.js'))
    .then(() => { window.gsap.registerPlugin(window.ScrollTrigger); return window.gsap; });
  return gsapPromise;
}

/* The element that actually scrolls the page. Horizon scrolls `.page-wrapper`
   on desktop (≥990px, the "squeeze" layout) and the document on phones. */
const squeeze = window.matchMedia('(min-width: 990px)');
function pageScroller() {
  const wrap = document.querySelector('.page-wrapper');
  if (wrap && squeeze.matches) {
    const oy = getComputedStyle(wrap).overflowY;
    if (oy === 'auto' || oy === 'scroll') return wrap;
  }
  return window;
}

/* Reveals waiting to run. If the shopper reaches the end of the page, anything
   still pending is shown at once, so no content can stay hidden. */
const pending = new Set();
function flushAtEnd() {
  if (!pending.size) return;
  const sc = pageScroller();
  const el = sc === window ? document.scrollingElement : sc;
  if (el.scrollTop + el.clientHeight >= el.scrollHeight - 8) [...pending].forEach((run) => run());
}
document.addEventListener('scroll', flushAtEnd, { capture: true, passive: true });

/* Run once when an element comes into view (works with any scroller) */
function whenVisible(el, cb, rootMargin = '0px 0px -18% 0px') {
  if (!el) return () => {};
  let done = false;
  const run = () => { if (done) return; done = true; io.disconnect(); pending.delete(run); cb(); };
  const io = new IntersectionObserver((entries) => { if (entries.some((e) => e.isIntersecting)) run(); }, { rootMargin });
  io.observe(el);
  pending.add(run);
  return () => { done = true; io.disconnect(); pending.delete(run); };
}

/* Pause continuous (CSS) animations while a section is off screen */
function watchVisibility(el) {
  const io = new IntersectionObserver(([entry]) => el.classList.toggle('is-idle', !entry.isIntersecting), { rootMargin: '10% 0px' });
  io.observe(el);
  return () => io.disconnect();
}

class CaveMotion extends HTMLElement {
  async connectedCallback() {
    this.cleanups = [watchVisibility(this)];
    if (reduceMotion.matches || this.dataset.motion === 'none') return;
    let gsap;
    try { gsap = await getGsap(); } catch (e) { return; } // no GSAP → static, still fully usable
    if (!this.isConnected) return;
    this.gsap = gsap;
    this.start();
    // Horizon switches the page scroller at 990px; rebuild on the new one
    this.onBreakpoint = () => { this.stop(); this.start(); window.ScrollTrigger?.refresh(); };
    squeeze.addEventListener('change', this.onBreakpoint);
  }
  start() {
    this.runs = [];
    this.scroller = pageScroller();
    this.ctx = this.gsap.context(() => this.build(this.gsap), this);
  }
  stop() {
    this.runs?.forEach((fn) => fn());
    this.runs = [];
    this.ctx?.revert();
    this.ctx = null;
  }
  disconnectedCallback() {
    squeeze.removeEventListener('change', this.onBreakpoint);
    this.cleanups?.forEach((fn) => fn());
    this.stop();
  }
  /* Register a once-only callback for when `el` is visible */
  onVisible(el, cb, rootMargin) {
    // Looked up when it fires: the context is assigned only after build() returns
    this.runs.push(whenVisible(el, () => (this.ctx ? this.ctx.add(cb) : cb()), rootMargin));
  }
  /* Fade/lift once when visible, then stay put */
  reveal(gsap, targets, trigger, opts = {}) {
    if (!targets.length) return;
    gsap.set(targets, { opacity: 0, y: opts.y ?? 18 });
    this.onVisible(trigger, () => {
      gsap.to(targets, { opacity: 1, y: 0, duration: opts.duration ?? 0.7, ease: 'power3.out', stagger: opts.stagger ?? 0.07, overwrite: 'auto' });
    }, opts.rootMargin);
  }
  st(config) { return { scroller: this.scroller, ...config }; }
  build() {}
}

/* ---------- Hero: CSS handles the entrance; GSAP adds restrained depth ---------- */
class CaveHero extends CaveMotion {
  build(gsap) {
    const img = this.querySelector('.cave-hero__img');
    if (!img) return;
    gsap.to(img, {
      yPercent: 5, scale: 0.985, ease: 'none',
      scrollTrigger: this.st({ trigger: this, start: 'top top', end: 'bottom top', scrub: 0.6 }),
    });
  }
}

/* ---------- Bundle contents: cards rise in turn, pictures settle ---------- */
class CaveKit extends CaveMotion {
  build(gsap) {
    this.reveal(gsap, [...this.querySelectorAll('.cave-head > *')], this);
    const cards = [...this.querySelectorAll('[data-card]')];
    this.reveal(gsap, cards, this.querySelector('.cave-items'), { stagger: 0.08, y: 22 });
    const pics = cards.map((c) => c.querySelector('.cave-item__media img')).filter(Boolean);
    if (pics.length) {
      gsap.set(pics, { scale: 0.92 });
      this.onVisible(this.querySelector('.cave-items'), () => gsap.to(pics, { scale: 1, duration: 1.1, ease: 'power3.out', stagger: 0.08 }));
    }
    this.reveal(gsap, [...this.querySelectorAll('.cave-items__foot')], this.querySelector('.cave-items__foot'));
  }
}

/* ---------- Component showcase ---------- */
class CaveShowcase extends CaveMotion {
  build(gsap) {
    const q = (s) => this.querySelector(s);
    const qa = (s) => [...this.querySelectorAll(s)];
    const slide = this.dataset.motion === 'slide';
    const fromSide = this.dataset.side === 'end' ? 1 : -1;
    const rtl = getComputedStyle(this).direction === 'rtl' ? -1 : 1;

    const stage = q('.cave-showcase__stage');
    const img = q('.cave-showcase__img');
    const line = q('.cave-annot__line');
    const tip = qa('.cave-annot__tip, .cave-annot__dot');
    const callout = qa('.cave-callout > *');
    const text = qa('.cave-showcase__content > *');
    const benefits = qa('.cave-showcase__points li');
    const panel = q('.cave-showcase__panel');

    const tl = gsap.timeline({ paused: true, defaults: { ease: 'power3.out' } });
    if (panel) tl.from(panel, { scaleY: 0.86, opacity: 0, transformOrigin: '50% 100%', duration: 0.9 }, 0);
    if (slide) tl.from(img, { xPercent: 12 * fromSide * rtl, rotate: -4 * fromSide, opacity: 0, duration: 1.3, ease: 'power4.out' }, 0.1);
    else tl.from(img, { yPercent: 10, opacity: 0, duration: 1.0 }, 0.1);
    tl.from(callout, { y: 12, opacity: 0, duration: 0.6, stagger: 0.08 }, '-=0.7');
    if (line) {
      tl.fromTo(line, { strokeDashoffset: 1 }, { strokeDashoffset: 0, duration: slide ? 1.0 : 0.8, ease: 'power2.inOut' }, '-=0.35');
      tl.from(tip, { opacity: 0, duration: 0.3 }, '-=0.2');
    }
    tl.from(text, { y: 18, opacity: 0, duration: 0.7, stagger: 0.07 }, slide ? 0.35 : 0.25);
    if (benefits.length) tl.from(benefits, { y: 10, opacity: 0, duration: 0.5, stagger: 0.06 }, '-=0.4');
    tl.progress(0.0001).pause(0); // apply the hidden starting state now
    this.onVisible(this, () => tl.play(), '0px 0px -28% 0px');

    // Restrained parallax on the whole stage — label, arrow and product move
    // together, so their spacing never changes.
    if (stage) {
      gsap.fromTo(stage, { y: 16 }, {
        y: -16, ease: 'none', immediateRender: false,
        scrollTrigger: this.st({ trigger: this, start: 'top bottom', end: 'bottom top', scrub: 0.8 }),
      });
    }
  }
}

/* ---------- Generic once-only reveal for simple sections ---------- */
class CaveReveal extends CaveMotion {
  build(gsap) {
    this.reveal(gsap, [...this.querySelectorAll('.cave-head > *')], this);
    this.querySelectorAll('[data-reveal]').forEach((el) => this.reveal(gsap, [el], el, { rootMargin: '0px 0px -8% 0px' }));
    // Photos drift a little against the scroll — image and frame move together
    this.querySelectorAll('[data-parallax]').forEach((el) => {
      const img = el.querySelector('img');
      if (!img) return;
      gsap.fromTo(img, { yPercent: -4, scale: 1.08 }, {
        yPercent: 4, scale: 1.08, ease: 'none', immediateRender: false,
        scrollTrigger: this.st({ trigger: el, start: 'top bottom', end: 'bottom top', scrub: 0.8 }),
      });
    });
  }
}

/* ---------- Value: session estimates count up once, gently ---------- */
class CaveValue extends CaveMotion {
  build(gsap) {
    this.reveal(gsap, [...this.querySelectorAll('[data-reveal]')], this, { rootMargin: '0px 0px -22% 0px', stagger: 0.08 });
    this.querySelectorAll('[data-count]').forEach((el, i) => {
      const end = parseInt(el.dataset.count, 10);
      if (!Number.isFinite(end)) return;
      this.onVisible(el, () => {
        const state = { v: 0 };
        el.textContent = '0';
        gsap.to(state, {
          v: end, duration: 1.6, delay: 0.25 + i * 0.15, ease: 'power2.out',
          onUpdate: () => { el.textContent = String(Math.round(state.v)); },
          onComplete: () => { el.textContent = String(end); },
        });
      }, '0px 0px -15% 0px');
    });
  }
}

/* ---------- Preparation steps: each step rises in order ---------- */
class CaveSteps extends CaveMotion {
  build(gsap) {
    this.reveal(gsap, [...this.querySelectorAll('.cave-head > *')], this);
    const steps = [...this.querySelectorAll('[data-step]')];
    steps.forEach((step) => this.reveal(gsap, [step], step, { y: 16 }));
    this.reveal(gsap, [...this.querySelectorAll('[data-reveal]')], this, { rootMargin: '0px 0px -10% 0px' });
  }
}

/* ---------- Before / after comparison (keyboard and touch via a native range) ---------- */
class CaveCompare extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('input[type="range"]');
    this.onInput = () => this.style.setProperty('--pos', `${this.input.value}%`);
    this.input?.addEventListener('input', this.onInput);
  }
  disconnectedCallback() { this.input?.removeEventListener('input', this.onInput); }
}

/* ---------- Sticky mobile purchase bar ---------- */
class CaveSticky extends HTMLElement {
  connectedCallback() {
    this.mobile = window.matchMedia('(max-width: 989px)');
    this.heroForm = document.querySelector('[data-cave-hero] .cave-buy');
    this.purchase = document.querySelector('[data-cave-purchase] .cave-buy__actions') || document.querySelector('[data-cave-purchase]');
    this.state = { heroSeen: true, heroAbove: false, purchaseSeen: false, typing: false };
    this.io = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.target === this.heroForm) {
          this.state.heroSeen = e.isIntersecting;
          this.state.heroAbove = !e.isIntersecting && e.boundingClientRect.top < 0;
        }
        if (e.target === this.purchase) this.state.purchaseSeen = e.isIntersecting;
      });
      this.update();
    });
    if (this.heroForm) this.io.observe(this.heroForm); else this.state.heroAbove = true;
    if (this.purchase) this.io.observe(this.purchase);

    // Never sit over a field the shopper is typing into (keyboard open)
    this.onFocusIn = (e) => { if (e.target.matches('input:not([type="hidden"]):not([type="range"]), select, textarea') && !this.contains(e.target)) { this.state.typing = true; this.update(); } };
    this.onFocusOut = () => { this.state.typing = false; this.update(); };
    document.addEventListener('focusin', this.onFocusIn);
    document.addEventListener('focusout', this.onFocusOut);
    this.onMobileChange = () => this.update();
    this.mobile.addEventListener('change', this.onMobileChange);

    // Keep the bar's price and button in step with the purchase form
    this.onVariant = (e) => {
      if (e.detail?.formId !== this.dataset.formId) return;
      const v = e.detail.variant;
      const price = this.querySelector('[data-sticky-price]');
      const btn = this.querySelector('[data-sticky-button]');
      if (price) price.textContent = v.price;
      if (btn) { btn.disabled = !v.purchasable; btn.textContent = e.detail.label; }
    };
    document.addEventListener('cave:variant', this.onVariant);
  }
  update() {
    const s = this.state;
    const show = this.mobile.matches && s.heroAbove && !s.heroSeen && !s.purchaseSeen && !s.typing;
    this.classList.toggle('is-shown', show);
    this.toggleAttribute('inert', !show);
    // Reserve room at the end of the page so the bar never covers the last content
    document.body.style.paddingBottom = show ? `${this.offsetHeight}px` : '';
  }
  disconnectedCallback() {
    this.io?.disconnect();
    this.mobile?.removeEventListener('change', this.onMobileChange);
    document.removeEventListener('focusin', this.onFocusIn);
    document.removeEventListener('focusout', this.onFocusOut);
    document.removeEventListener('cave:variant', this.onVariant);
    document.body.style.paddingBottom = '';
  }
}

const define = (name, cls) => { if (!customElements.get(name)) customElements.define(name, cls); };
define('cave-hero', CaveHero);
define('cave-kit', CaveKit);
define('cave-showcase', CaveShowcase);
define('cave-reveal', CaveReveal);
define('cave-steps', CaveSteps);
define('cave-value', CaveValue);
define('cave-compare', CaveCompare);
define('cave-sticky', CaveSticky);

/* Theme Editor: recalc trigger positions when sections are added, moved or reordered */
['shopify:section:load', 'shopify:section:unload', 'shopify:section:reorder'].forEach((ev) =>
  document.addEventListener(ev, () => requestAnimationFrame(() => window.ScrollTrigger?.refresh()))
);


class CaveOrbit extends CaveMotion {
  build(gsap) {
    const box = this.querySelector('[data-kind="box"]');
    if (!box) return;
    this.querySelectorAll('[data-card]:not([data-kind="box"])').forEach((item) => {
      this.onVisible(item, () => {
        const a = item.getBoundingClientRect(), b = box.getBoundingClientRect();
        gsap.fromTo(item, {x:(b.left+b.width/2-a.left-a.width/2)*.25,y:24,opacity:0,scale:.84}, {x:0,y:0,opacity:1,scale:1,duration:1.1,ease:'power3.out',clearProps:'transform,opacity'});
      });
    });
  }
}
if (!customElements.get('cave-orbit')) customElements.define('cave-orbit', CaveOrbit);
